import { tryPromise } from '@woonstad/helpers-utils';
import { useEffect, useState } from 'react';

/**
 * Util for simple data fetching using the generated API clients.
 *
 * Alternative to react-query, SWR, etc.
 *
 * This makes it easier to fetch data, without the cache layer, providers, etc.
 *
 * @param fetchFn Provide the generated API client function.
 * @returns `{ data, status, error }`
 *
 * @example const { data } = useAPIClient(token ? UserService.userMe : async () => undefined);
 */
export const useAPIClient = <Data = any>(fetchFn: (...args: any) => Promise<Data>) => {
  const [data, setData] = useState<Data | undefined>(undefined);
  const [status, setStatus] = useState<'stale' | 'loading' | 'success' | 'error'>('stale');
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      setStatus('loading');

      const {
        data,
        error,
      } = await tryPromise(fetchFn);

      if (error) {
        setStatus('error');
        setError(error);

        return;
      }

      setData(data);
      setStatus('success');
    };

    fetchData();
  }, [fetchFn]);

  return {
    data,
    status,
    error,
  };
};
